/**
 * What must never leave a customer's account in a log line.
 *
 * The control-plane/data-plane split is the whole value proposition: Qrvey
 * structurally cannot see a customer's data. Run logs are the most likely place
 * for that to stop being true, because Terraform prints what it is given and the
 * installer is given credentials, registry keys and an administrator's email.
 *
 * ## Where this runs
 *
 * Inside the customer's account, in the runner container, **before the line
 * reaches the log driver**. Not in the control plane on the way in: by then the
 * data has already left their account, which is the thing the criterion forbids.
 * Redacting at the source also means their *own* log group never holds the
 * secrets either — stronger than asked, and the reason nobody has to trust our
 * reader.
 *
 * ## Fail closed
 *
 * The wrapper self-tests the redactor before starting the installer and refuses
 * to run if a seeded secret survives. If the redactor dies mid-run the pipe
 * breaks and the installer dies with it. Both are deliberate: a run that fails
 * is recoverable, and a run that ships unredacted logs is not.
 *
 * ## Reviewable
 *
 * The rules are data, not code, and the wrapper carrying them is in the template
 * the customer deploys — so a security team can read exactly what is matched
 * without taking our word for it. `docs/log-redaction.md` is the same list in
 * prose.
 *
 * False positives are accepted deliberately. `high-entropy` masks any long
 * base64-ish run, which catches secret keys, session tokens and private-key
 * bodies — and sometimes a checksum. A masked checksum costs a support engineer
 * a moment; an unmasked session token costs a customer their account.
 */
/**
 * Which rule set this build ships, so a customer's account can say which it runs.
 *
 * The redactor is inlined in the dispatcher template, which is what lets a
 * security team read it — and what freezes it at whatever was current when
 * that stack was deployed. A rule added here reaches nobody until they update,
 * and until IM-84 nothing could tell a current redactor from a year-old one:
 * the in-account self-test checks the deployed rules against the deployed
 * fixtures, which agree with each other at any age.
 *
 * **Bump it whenever a pattern or a replacement changes**, and record the new
 * fingerprint in `fixtures/redaction-rules.json`; `redaction-version.test.ts`
 * fails on a rule change that does neither. Rewording a `why` is not a rule
 * change and needs no bump.
 *
 * Published three ways: the dispatcher stack's `RedactionRulesVersion` output,
 * which the account screen reads per region; the self-test's line near the top
 * of every run's log, which ties one log to the rules that masked it; and
 * `docs/log-redaction.md`.
 */
export const REDACTION_RULES_VERSION = '1';
/**
 * Stacks deployed before the version was published, and what they are known to
 * carry.
 *
 * v1 is the rule set as it stood after IM-81, and dispatcher v5 is the first
 * template published after it — so a v5 to v10 stack has no output and runs
 * exactly v1. v4 is the one that cannot be placed: IM-81 changed the rules
 * without bumping it, which is this ticket's whole point in one example, so a
 * v4 stack may carry either. Anything below v5 is therefore reported as older
 * than v1, which is true of every one of them except some v4s, and the cost of
 * that error is asking someone to update a stack that is behind anyway.
 */
export const UNVERSIONED_REDACTION = {
    fromDispatcherVersion: '5',
    rulesVersion: '1',
};
export const REDACTION_RULES = [
    {
        name: 'aws-access-key-id',
        pattern: '\\b(?:AKIA|ASIA|AIDA|AROA|AGPA|ANPA|ANVA|APKA)[0-9A-Z]{16}\\b',
        replacement: '[redacted:aws-access-key-id]',
        why: 'AWS key ids are recognisable on sight and identify the principal a run acted as.',
    },
    {
        name: 'named-secret-assignment',
        /**
         * The name must be a whole `_`-separated identifier, one of whose words is a
         * secret word — not merely a string containing one.
         *
         * The first version matched any run of letters around the word, which ate
         * ordinary Terraform output: `data.aws_kms_alias.secretsmanager: Reading...`
         * became `...secretsmanager: [redacted]`, and so did every
         * `authorization_token.token: Reading...`. Masking a verb costs the reader
         * the line, which is the failure a redactor is supposed to avoid.
         *
         * `secretsmanager` no longer matches because `secret` is followed by `s`
         * rather than `_` or the end; `aws_secret_access_key` still does. The
         * leading lookbehind keeps `.token` from matching while `registry_key = x`
         * still does — a dotted attribute is a reference, not an assignment.
         *
         * **The compound words accept a space or hyphen, not only `_`.** The
         * installer's own end-of-run banner does not print identifiers, it prints
         * human labels — `printf "%-25s %s\n" "Qrvey API Key:" "$value"`. So
         * `api_key` matched nothing there and the key went to the log in full. The
         * single words never had this problem: `Admin Password:` already matched on
         * `password` alone.
         */
        pattern: '(?i)(?:(?<=^)|(?<=[\\s"\'{,\\[]))((?:[A-Za-z0-9]+_)*(?:password|passwd|secret|session[ _]token|token|api[ _-]?key|private[ _]key|registry[ _]key|client[ _]secret)(?:_[A-Za-z0-9]+)*)(\\s*[:=]\\s*)\\S+',
        replacement: '\\1\\2[redacted]',
        why: 'Anything a line labels as a secret is treated as one, whatever its shape — including names like aws_secret_access_key.',
    },
    {
        name: 'bearer-token',
        pattern: '(?i)\\bbearer\\s+[A-Za-z0-9._~+/-]+=*',
        replacement: 'Bearer [redacted]',
        why: 'An Authorization header echoed into a log is a usable credential.',
    },
    {
        name: 'private-key-header',
        pattern: '-----BEGIN[^-]*PRIVATE KEY-----',
        replacement: '[redacted:private-key]',
        why: 'The body is caught by high-entropy; this removes the marker that makes it findable.',
    },
    {
        name: 'webhook-url',
        pattern: 'https://hooks\\.slack\\.com/services/\\S+',
        replacement: '[redacted:webhook-url]',
        why: 'The installer image carries live Slack webhooks; a webhook URL is itself the credential.',
    },
    {
        name: 'uri-credentials',
        /**
         * Ordered before `email-local-part` deliberately: that rule would otherwise
         * reach `password@host` first and rewrite the part of it that happens to
         * look like an address, leaving whatever preceded the first `@`-illegal
         * character behind. `pg_connection_string` leaked its first three
         * characters that way.
         */
        pattern: '([a-zA-Z][a-zA-Z0-9+.-]*://[^\\s:/@]+):[^\\s@]+@',
        replacement: '\\1:[redacted]@',
        why: 'A connection string carries its password inline, where no label names it as one.',
    },
    {
        name: 'high-entropy',
        pattern: '[A-Za-z0-9+/]{40,}={0,2}',
        replacement: '[redacted:high-entropy]',
        why: 'Catches secret access keys, session tokens and private-key bodies, which have no distinguishing prefix.',
    },
    {
        name: 'email-local-part',
        pattern: '[A-Za-z0-9._%+-]+@([A-Za-z0-9.-]+\\.[A-Za-z]{2,})',
        replacement: '[redacted:email]@\\1',
        why: 'Who a person is, is theirs. The domain is kept because it tells a support engineer which customer without naming anyone.',
    },
];
/**
 * Seeded secrets the redactor is checked against before every run.
 *
 * Deliberately realistic in shape and worthless in fact. If any of these
 * survives, the run does not start.
 */
export const REDACTION_FIXTURES = [
    'aws_access_key_id = "AKIAIOSFODNN7EXAMPLE"',
    'aws_secret_access_key = "wJalrXUtnFEMIK7MDENGbPxRfiCYEXAMPLEKEYAB"',
    'registry_key = "swordfish"',
    'authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9',
    '-----BEGIN RSA PRIVATE KEY-----',
    'SLACK_WEBHOOK_URL=https://hooks.slack.com/services/T00/B00/XXXXXXXXXXXXXXXXXXXXXXXX',
    'initial_admin_email = "someone@customer.example"',
    /**
     * The installer's end-of-run banner, in its exact `printf "%-25s %s"` shape.
     *
     * These print on every successful apply, not only on `output` — and two of
     * them were leaking. The labels are human text, so `api_key` never matched
     * `Qrvey API Key:`, and the connection string's password was reached by the
     * email rule instead of a rule that meant to.
     */
    'Qrvey API Key:            qk_live_ExampleApiKeyValue',
    'PostgreSQL Connection:    postgres://qrvey_usr:Pa#ssw0rd-example@db.cluster-x.us-east-1.rds.amazonaws.com:5432/postgres',
    // The same row with a `/` in the password. Generated passwords contain them,
    // and one used to defeat `uri-credentials` outright — the rule stopped at the
    // slash, so the email rule took the tail and left the head in the log.
    'PostgreSQL Connection:    postgres://qrvey_usr:sl4sh/ed-example@db.cluster-y.us-east-1.rds.amazonaws.com:5432/postgres',
    'Admin Password:           Tr0ub4dor-example',
    'Elasticsearch Password:   esPassw0rd-example',
    'RabbitMQ Password:        rmqPassw0rd-example',
    'Grafana Password:         grafPassw0rd-example',
];
/**
 * Real lines from a real plan that must come through untouched.
 *
 * Taken from the first end-to-end run, where the rules ate the verb out of every
 * `secretsmanager: Reading...` line. A redactor is judged on both directions and
 * only one of them was being checked.
 */
export const REDACTION_MUST_SURVIVE = [
    'data.aws_kms_alias.secretsmanager: Reading...',
    'data.aws_ecrpublic_authorization_token.token: Reading...',
    'data.aws_kms_alias.secretsmanager: Read complete after 0s',
    'module.eks.aws_eks_cluster.this: Still creating... [10m0s elapsed]',
    // The rest of the same banner. Widening the rules to reach its secrets must
    // not cost the rows someone actually reads it for.
    'Environment URL:          https://acme.qrveyapp.com',
    'Admin Portal URL:         https://acme.qrveyapp.com/admin',
    'Load Balancer URL:        a1b2c3.elb.us-east-1.amazonaws.com',
    'Admin User:               admin',
    'Deployment ID:            abcde',
    'Qrvey Version Deployed:   9.5.0',
    /**
     * A real banner row carrying a port and a path, from the 2026-09-10 run.
     *
     * Here because widening the connection-string rule to span `/` makes a URL
     * with a port look more like `scheme://host:password@` than it did. This one
     * has no `@`, so the rule cannot reach it — and this fixture is what keeps
     * that true.
     */
    'RabbitMQ Host:            http://af9f2356707eb4d2-1570825199.us-east-1.elb.amazonaws.com:15672/#/',
    /*
     * The installer's verdict on an exit 10 or 11 (MP-3615), which the console
     * headlines (IM-186). Fixed strings from the image's `post_apply.sh`, and the
     * one part of a failed run's log that says what to do — so a future rule that
     * masked any of it would hide the answer, not a secret.
     */
    '2026-10-08T13:20:00Z ERROR Post-apply verification did not pass (exit 10):',
    '2026-10-08T13:20:00Z ERROR   - V1: could not configure Kubernetes access after apply',
    '2026-10-08T13:20:00Z ERROR   - V4: gateway routes still failing after the remediation re-apply',
    '2026-10-08T13:20:00Z ERROR   - V6: OpenSearch migration job timed out after 7200s',
    '2026-10-08T13:20:00Z ERROR   - V7: cluster unreachable for the final output',
];
/** Words that must not survive the fixtures above. */
export const REDACTION_FORBIDDEN = [
    'AKIAIOSFODNN7EXAMPLE',
    'wJalrXUtnFEMIK7MDENGbPxRfiCYEXAMPLEKEYAB',
    'swordfish',
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9',
    'PRIVATE KEY',
    'T00/B00',
    'someone@',
    'qk_live_ExampleApiKeyValue',
    // Only the leading fragment of the connection password: the tail was already
    // being masked, by accident, and a partial leak is still a leak.
    'Pa#',
    // Both halves of the slash-containing password. The head is what leaked.
    'sl4sh/',
    'ed-example',
    'Tr0ub4dor-example',
    'esPassw0rd-example',
    'rmqPassw0rd-example',
    'grafPassw0rd-example',
];
const asJson = (value) => JSON.stringify(value);
/**
 * The redactor, as a Python program.
 *
 * Python because the rules stay legible as a list a security team can read;
 * a sed chain of the same expressions would be write-only. Both images that
 * matter carry python3.
 *
 * Line-buffered and streaming: a run's log has to appear while it is running,
 * not when it ends.
 */
export const REDACTOR_SOURCE = `import json, re, sys

RULES = [(re.compile(p), r) for p, r in json.loads(r'''${asJson(REDACTION_RULES.map((rule) => [rule.pattern, rule.replacement]))}''')]

def redact(line):
    for pattern, replacement in RULES:
        line = pattern.sub(replacement, line)
    return line

if len(sys.argv) > 1 and sys.argv[1] == '--self-test':
    seeded = json.loads(r'''${asJson(REDACTION_FIXTURES)}''')
    forbidden = json.loads(r'''${asJson(REDACTION_FORBIDDEN)}''')
    cleaned = '\\n'.join(redact(line) for line in seeded)
    leaked = [word for word in forbidden if word in cleaned]
    if leaked:
        sys.stderr.write('redactor self-test failed: ' + ', '.join(leaked) + '\\n')
        sys.exit(1)
    sys.stderr.write('redactor self-test passed (rules v${REDACTION_RULES_VERSION})\\n')
    sys.exit(0)

for line in sys.stdin:
    sys.stdout.write(redact(line))
    sys.stdout.flush()
`;
//# sourceMappingURL=redaction.js.map